<template>
  <div class="wishlist">
    <loader v-if="is.initial" variant="primary" />

    <c-leaf v-else-if="basket.itemCount <= 0" class="wishlist-emptymessage">
      <b-row align-h="center" align-v="center">
        <b-col cols="auto">
          <div class="emptymessage-icon">
            <b-icon icon="b-heart" font-scale="8" />
          </div>
        </b-col>

        <b-col :cols="!$root.isMobile ? 'auto' : 12">
          <div class="emptymessage-content">
            <slot name="emptymessage" />
          </div>
        </b-col>
      </b-row>
    </c-leaf>

    <div v-else>
      <h4>{{ $t(`${tPath}.wishlist.overview.title`, { count: basket.itemCount }) }}</h4>

      <b-row :cols="contentCols.xs" :cols-sm="contentCols.sm" :cols-md="contentCols.md" :cols-lg="contentCols.lg" :cols-xl="contentCols.xl">
        <b-col v-for="item in (basket.items || [])" :key="item.basketItemId" class="wishlist-item-col">
          <c-teaser
                    :href="productHref(item)"
                    img-type="product"
                    :img-src="productImage(item)"
                    :img-alt="productTitle(item)"
                    :hideArrow="isLoggedIn && !!productId(item)">
            <h3><a class="teaser-link stretched-link" :href="productHref(item)">{{ productTitle(item) }}</a></h3>
            <p v-if="productPortioning(item)">{{ productPortioning(item) }}</p>

            <template #overlay-controls v-if="isLoggedIn && productId(item)">
              <c-wish-list-basket-button
                                         :productId="productId(item)"
                                         :basketButtonText="translation('wishlistbasketbuttontext')"
                                         :basketButtonRemoveText="translation('wishlistbasketremovebuttontext')" />
            </template>

            <template #footer-controls v-if="isLoggedIn && productId(item)">
              <div class="search-product-purchase justify-content-between flex-sm-column flex-xl-row justify-content-sm-center justify-content-xl-between align-items-center">
                <div class="d-flex align-items-left align-self-sm-start align-self-xl-center">
                  <div class="purchase-availability">
                    <b-icon v-if="isOutOfStock(item)" icon="b-x-circle-fill" class="availability-icon not-available" :title="translation('notavailable')" />
                    <b-icon v-else icon="b-check-circle-fill" class="availability-icon available" :title="translation('available')" />
                  </div>
                  <div class="purchase-price">
                    <span class="price-value">{{ formatPrice(priceValue(item)) }}</span>
                  </div>
                </div>
                <div class="purchase-cart align-self-sm-end align-self-xl-center ml-sm-0">
                  <c-product-basketbutton
                                          v-if="isAvailable(item)"
                                          class="purchase-basket-select"
                                          :productId="productId(item)"
                                          :small="true"
                                          :translations="productBuyButtonTranslations"
                                          :useI18n="true">
                    <b-icon icon="cart" :title="translation('addtobasket')" />
                  </c-product-basketbutton>
                  <b-button
                    v-else-if="isOutOfStock(item)"
                    class="purchase-action-button"
                    :class="isRestockSubscribedFor(item) ? 'is-primary' : 'is-muted'"
                    :title="translation('notifyme')"
                    @click.stop.prevent="onNotifyClick(item)"
                  >
                    <b-icon icon="b-bell-fill" />
                  </b-button>
                  <b-button v-else-if="isNotOrderableEligibility(item)" class="purchase-action-button is-muted" :title="translation('notorderableeligibility')" disabled @click.stop.prevent>
                    <b-icon icon="cart" />
                  </b-button>
                  <b-button v-else class="purchase-action-button is-muted" :title="getStatusText(item)" disabled @click.stop.prevent>
                    <b-icon icon="cart" />
                  </b-button>
                </div>
              </div>
            </template>

          </c-teaser>
        </b-col>
      </b-row>

      <div class="items-footer">
        <div class="footer-actions">
          <b-link @click.prevent="deleteBasket"><b-icon icon="trash" /> {{ $t(`${tPath}.wishlist.deletebasket`) }}</b-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ASIDE_COLUMN_WIDTHS } from '@/assets/js/config/client';
import Loader from '@/components/Loader';
import { PRODUCT_ORDER_STATUS } from '@/constants';
import { getProductRestockSubscriptionStatus, toggleProductRestockSubscription } from '@/assets/js/helper/product-restock-subscription';

export default {
  name: 'WishListOverview',
  components: {
    Loader
  },
  computed: {
    is() {
      return this.$store.getters['wishlistcart/is']
    },
    basket() {
      return this.$store.getters['wishlistcart/get']
    },
    isLoggedIn() {
      return !!this.$store.getters['profile/getUserId']
    },
    productBuyButtonTranslations() {
      return {
        customQuantityLabel: this.translation('productbuybutton.customquantitylabel'),
        marketingProductsModalTitle: this.translation('productbuybutton.marketingproductsmodaltitle'),
        marketingProductsModalCloseLabel: this.translation('productbuybutton.marketingproductsmodalcloselabel'),
        deliveryTimeSlot: this.translation('productbuybutton.deliverytimeslot'),
        marketingproductdetail_nodateselected: this.translation('productbuybutton.marketingproductdetail.nodateselected'),
        marketingproductdetail_errortitle: this.translation('productbuybutton.marketingproductdetail.errortitle')
      }
    }
  },
  data() {
    return {
      contentCols: ASIDE_COLUMN_WIDTHS,
      PRODUCT_ORDER_STATUS,
      restockSubscribedByProductId: {}
    }
  },
  watch: {
    'basket.items': {
      deep: true,
      handler: 'refreshRestockSubscriptionStatuses'
    },
    isLoggedIn: 'refreshRestockSubscriptionStatuses'
  },
  methods: {
    translation(key, fallback = '') {
      const path = `${this.tPath}.${key}`
      const translated = this.$t(path)
      return translated === path ? fallback : translated
    },
    productId(item) {
      const p = item?.product || {}
      return p.id || item?.productExternalId || null
    },
    productTitle(item) {
      const p = item?.product || {}
      return p.name || p.title || p.label || p.id || ''
    },
    productImage(item) {
      const p = item?.product || {}
      return p.image || p.imgSrc || p.img || null
    },
    productHref(item) {
      const p = item?.product || {}
      const urlSlug = p.urlSlug || p.urlslug || ''
      if (urlSlug) return this.$store.getters['gui/getBaseUrl']('product', urlSlug)
      return p.href || p.url || '#'
    },
    productPortioning(item) {
      const p = item?.product || {}
      return p.portioning || ''
    },
    getPurchase(item) {
      const purchase = item?.purchase || item?.product?.purchase
      if (purchase) return purchase

      const p = item?.product || {}
      const hasEligibilityData = typeof p.isEligibleForPurchase === 'boolean' || p.stockAmount !== undefined
      if (!hasEligibilityData) return {}

      const stockAmount = Number(p.stockAmount || 0)
      const orderStatus = p.isEligibleForPurchase === false
        ? PRODUCT_ORDER_STATUS.NOT_ORDERABLE_ELIGIBILITY
        : (stockAmount > 0 ? PRODUCT_ORDER_STATUS.AVAILABLE : PRODUCT_ORDER_STATUS.OUT_OF_STOCK)

      return {
        hasEligibilityData,
        isEligibleForPurchase: p.isEligibleForPurchase,
        stockAmount,
        orderStatus,
        priceinfo: {
          currency: 'CHF',
          price: p.exFactoryPrice !== undefined && p.exFactoryPrice !== null ? Number(p.exFactoryPrice) : 0,
          priceUnassembled: 0,
          priceOriginal: p.uvpPrice !== undefined && p.uvpPrice !== null ? Number(p.uvpPrice) : 0
        }
      }
    },
    isBuyableResult(item) {
      return !!this.productId(item)
    },
    isAvailable(item) {
      const orderStatus = this.getPurchase(item).orderStatus
      if (!orderStatus) return true
      return orderStatus === PRODUCT_ORDER_STATUS.AVAILABLE
    },
    isOutOfStock(item) {
      return this.getPurchase(item).orderStatus === PRODUCT_ORDER_STATUS.OUT_OF_STOCK
    },
    isNotOrderableEligibility(item) {
      return this.getPurchase(item).orderStatus === PRODUCT_ORDER_STATUS.NOT_ORDERABLE_ELIGIBILITY
    },
    getStatusText(item) {
      const purchase = this.getPurchase(item)
      if (!purchase) return null

      if (purchase.orderStatus === PRODUCT_ORDER_STATUS.LOCATION_REQUIRED) {
        return this.translation('orderlocationrequired')
      }
      if (purchase.orderStatus === PRODUCT_ORDER_STATUS.NOT_ORDERABLE_ELIGIBILITY) {
        return this.translation('notorderableeligibility')
      }
      if (purchase.orderStatus === PRODUCT_ORDER_STATUS.OUT_OF_STOCK) {
        return this.translation('notifyme')
      }

      return null
    },
    isRestockSubscribedFor(item) {
      const id = this.productId(item)
      if (!id) return false
      return !!this.restockSubscribedByProductId[id]
    },
    async refreshRestockSubscriptionStatuses() {
      if (!this.isLoggedIn || !this.basket?.items?.length) {
        this.restockSubscribedByProductId = {}
        return
      }

      const ids = [
        ...new Set(
          this.basket.items
            .filter(item => this.isOutOfStock(item) && this.productId(item))
            .map(item => this.productId(item))
        )
      ]
      if (!ids.length) {
        this.restockSubscribedByProductId = {}
        return
      }
      const entries = await Promise.all(
        ids.map(async (id) => {
          const { subscribed } = await getProductRestockSubscriptionStatus(id)
          return [id, subscribed]
        })
      )
      const next = {}
      entries.forEach(([id, subscribed]) => {
        if (subscribed) next[id] = true
      })
      this.restockSubscribedByProductId = next
    },
    async onNotifyClick(item) {
      const id = this.productId(item)
      if (!this.isLoggedIn || !id || !this.isOutOfStock(item)) {
        return
      }

      const toggleResult = await toggleProductRestockSubscription({
        productExternalId: id,
        toast: this.$bvToast,
        t: key => this.translation(key.toLowerCase(), this.$t(key))
      })
      if (toggleResult.success && typeof toggleResult.subscribed === 'boolean') {
        this.$set(this.restockSubscribedByProductId, id, toggleResult.subscribed)
      }
    },
    priceValue(item) {
      const purchase = this.getPurchase(item)
      if (purchase?.priceinfo?.price !== undefined) return purchase.priceinfo.price

      const p = item?.product || {}
      if (p?.priceinfo?.price !== undefined) return p.priceinfo.price
      if (p?.price !== undefined) return p.price
      if (p?.exFactoryPrice !== undefined) return p.exFactoryPrice
      if (item?.price?.singlePrice !== undefined) return item.price.singlePrice

      return 0
    },
    formatPrice(value) {
      const normalized = Number(value)
      if (Number.isNaN(normalized)) return 'CHF 0.00'
      return `CHF ${normalized.toFixed(2)}`
    },
    deleteBasket() {
      this.$store.dispatch('wishlistcart/delete')
    },
    deleteItem(item) {
      const p = item?.product || {}
      const productId = p.id || item?.productExternalId || null
      if (productId) return this.$store.dispatch('wishlistcart/removeItem', { productId, omitLastAction: true })
      return Promise.resolve()
    }
  },
  created() {
    this.$store.dispatch('wishlistcart/get')
    this.refreshRestockSubscriptionStatuses()
  }
}
</script>

<style lang="scss">
.wishlist {
  @include component('y');

  .teaser-link {
    color: $body-color;
    text-decoration: none;
  }

  .card-footer {
    padding-right: 1rem;
    padding-left: 1rem;
    z-index: 2;
  }

  .items-footer {
    margin-top: $spacer;
    display: flex;
    justify-content: flex-end;
  }

  .wishlist-item-col {
    margin-bottom: $spacer * 1.5;
  }

  .search-product-purchase {
    margin-top: 0;
    display: flex;
    align-items: center;
    gap: $spacer * 0.35;
    flex-wrap: nowrap;
    min-width: 0;
    width: 100%;

    .purchase-availability {
      margin-right: $spacer * 0.5;
    }

    .purchase-price {
      white-space: nowrap;

      .price-value {
        color: $gray-700;
      }
    }

    .availability-icon.available {
      color: $success;
    }

    .availability-icon.not-available {
      color: $black;
    }

    .purchase-cart {
      display: inline-flex;
      align-items: center;
      margin-left: $spacer * 0.15;
    }

    .purchase-basket-select {
      overflow: visible;
    }

    .purchase-basket-select .basketbutton-text {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding-left: $spacer * 0.35;
      padding-right: $spacer * 0.35;
    }

    .purchase-basket-select .basketbutton-text .bi {
      font-size: 1rem;
    }

    .purchase-basket-select .dropdown-toggle {
      min-height: 1.55rem;
      padding-top: 0;
      padding-bottom: 0;
    }

    .purchase-action-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2.1rem;
      height: 2.1rem;
      padding: 0;
      border-radius: $border-radius;

      &.is-primary {
        background-color: $primary;
        border-color: $primary;
        color: color-yiq($primary);
      }

      &.is-muted {
        background-color: $gray-500;
        border-color: $gray-500;
        color: $white;
      }
    }
  }
}
</style>
